import React, { useState } from "react";
import Chart from "react-apexcharts";
import { format, parseISO } from "date-fns";
import fr from "date-fns/locale/fr";

const ProspectStatsChart = ({ prospectsData }) => {
  const [periodType, setPeriodType] = useState("mois");
  const [selectedYear, setSelectedYear] = useState(null);

  const processData = () => {
    if (
      !prospectsData ||
      !prospectsData.week ||
      !prospectsData.month ||
      !prospectsData.year
    ) {
      return { categories: [], series: [] };
    }

    let categories = [];
    let seriesData = [];

    if (periodType === "année" && !selectedYear) {
      // Mode affichage des années
      // Regrouper les données par année pour éviter les doublons
      const yearMap = new Map();

      if (prospectsData.year.labels && prospectsData.year.data) {
        prospectsData.year.labels.forEach((label, index) => {
          // Le label est déjà le nom du mois dans l'année
          if (yearMap.has(label)) {
            yearMap.set(
              label,
              yearMap.get(label) + prospectsData.year.data[index]
            );
          } else {
            yearMap.set(label, prospectsData.year.data[index]);
          }
        });
      }

      categories = Array.from(yearMap.keys());
      seriesData = Array.from(yearMap.values());
    } else if (periodType === "année" && selectedYear) {
      // Mode affichage des mois pour une année sélectionnée
      // On doit extraire les mois de l'année sélectionnée depuis les données mensuelles
      const yearData = prospectsData.month;
      const yearMonths = [];
      const yearValues = [];

      // Liste complète des mois en français abrégés
      const allMonths = [
        "janvier",
        "février",
        "mars",
        "avril",
        "mai",
        "juin",
        "juillet",
        "août",
        "septembre",
        "octobre",
        "novembre",
        "décembre",
      ];

      // Pour chaque mois, vérifier s'il appartient à l'année sélectionnée
      allMonths.forEach((month, index) => {
        // Trouver l'index de ce mois dans les labels
        const monthIndex = yearData.labels.findIndex((label) =>
          label.toLowerCase().includes(month.toLowerCase())
        );

        if (monthIndex !== -1) {
          yearMonths.push(month.substring(0, 3)); // Prendre les 3 premières lettres
          yearValues.push(yearData.data[monthIndex]);
        } else {
          yearMonths.push(month.substring(0, 3));
          yearValues.push(0);
        }
      });

      categories = yearMonths;
      seriesData = yearValues;
    } else if (periodType === "mois") {
      // Mode affichage par mois
      // Regrouper les mêmes mois ensemble
      const monthMap = new Map(); // Pour stocker les données par mois

      if (prospectsData.month.labels && prospectsData.month.data) {
        prospectsData.month.labels.forEach((label, index) => {
          // Essayer de convertir le label en date pour en extraire le mois de façon fiable
          // Différentes tentatives pour gérer plusieurs formats possibles
          let monthKey;

          try {
            if (label.includes("-")) {
              // Format possible: "2025-06-20"
              const dateParts = label.split("-");
              if (dateParts.length >= 2) {
                const date = new Date(
                  dateParts[0],
                  parseInt(dateParts[1]) - 1,
                  1
                );
                monthKey = date.toLocaleString("fr-FR", { month: "long" });
              }
            } else if (label.includes("/")) {
              // Format possible: "20/06/2025"
              const dateParts = label.split("/");
              if (dateParts.length >= 2) {
                const date = new Date(
                  dateParts[2],
                  parseInt(dateParts[1]) - 1,
                  1
                );
                monthKey = date.toLocaleString("fr-FR", { month: "long" });
              }
            } else {
              // Format possible: "20 juin 2025"
              const parts = label.split(" ");
              if (parts.length >= 2) {
                monthKey = parts[1].toLowerCase(); // On utilise le mois comme clé
              }
            }
          } catch (error) {
            console.error("Erreur lors de l'extraction du mois:", error);
          }

          // Si on n'a pas réussi à extraire le mois, utiliser le label complet
          if (!monthKey) {
            monthKey = label;
          }

          // Si le mois existe déjà, ajouter la valeur
          if (monthMap.has(monthKey)) {
            monthMap.set(
              monthKey,
              monthMap.get(monthKey) + prospectsData.month.data[index]
            );
          } else {
            // Sinon, créer une nouvelle entrée
            monthMap.set(monthKey, prospectsData.month.data[index]);
          }
        });
      }

      // Convertir la Map en tableaux pour le graphique
      categories = Array.from(monthMap.keys());
      seriesData = Array.from(monthMap.values());
    } else if (periodType === "semaine") {
      // Mode affichage par semaine
      // On peut également regrouper par jour de la semaine si nécessaire
      const weekMap = new Map();

      if (prospectsData.week.labels && prospectsData.week.data) {
        prospectsData.week.labels.forEach((label, index) => {
          // Pour les semaines, on conserve le label complet car on veut distinguer les jours
          // Mais on pourrait extraire juste le jour ou le jour+mois selon le besoin
          const dayKey = label;

          if (weekMap.has(dayKey)) {
            weekMap.set(
              dayKey,
              weekMap.get(dayKey) + prospectsData.week.data[index]
            );
          } else {
            weekMap.set(dayKey, prospectsData.week.data[index]);
          }
        });
      }

      categories = Array.from(weekMap.keys());
      seriesData = Array.from(weekMap.values());
    }

    return {
      categories,
      series: [
        {
          name: "Nombre de prospects",
          data: seriesData,
        },
      ],
    };
  };

  const { categories, series } = processData();

  const handleYearClick = (year) => {
    setSelectedYear(year === selectedYear ? null : year);
  };

  const chartOptions = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          if (periodType === "année" && !selectedYear) {
            const year = categories[config.dataPointIndex];
            handleYearClick(year);
          }
        },
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: periodType === "année" && !selectedYear ? "70%" : "50%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories,
      labels: {
        style: {
          fontSize: "12px",
          fontFamily: "Inter, sans-serif",
        },
      },
    },
    yaxis: {
      title: {
        text: "Nombre de prospects",
      },
      labels: {
        formatter: (value) => (Math.floor(value) === value ? value : ""),
      },
    },
    fill: {
      opacity: 1,
      colors: ["#D97706"],
    },
    colors: ["#D97706"],
    tooltip: {
      y: {
        formatter: function (val) {
          return val + " prospects";
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            height: 300,
          },
          xaxis: {
            labels: {
              rotate: -45,
            },
          },
        },
      },
    ],
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-2 md:mb-0">
          Nombre de prospects enregistrés par le commercial
        </h2>

        <div className="flex space-x-2">
          <button
            onClick={() => {
              setPeriodType("année");
              setSelectedYear(null);
            }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${
              periodType === "année"
                ? "bg-amber-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Année
          </button>
          <button
            onClick={() => {
              setPeriodType("mois");
              setSelectedYear(null);
            }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${
              periodType === "mois"
                ? "bg-amber-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Mois
          </button>
          <button
            onClick={() => {
              setPeriodType("semaine");
              setSelectedYear(null);
            }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${
              periodType === "semaine"
                ? "bg-amber-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Semaine
          </button>
        </div>
      </div>

      {periodType === "année" && selectedYear && (
        <div className="mb-4 flex items-center">
          <button
            onClick={() => setSelectedYear(null)}
            className="flex items-center text-amber-600 hover:text-amber-800 mr-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            <span className="ml-1">Retour aux années</span>
          </button>
          <span className="text-lg font-medium">{selectedYear}</span>
        </div>
      )}

      <div className="mt-4">
        {series.length > 0 && categories.length > 0 ? (
          <Chart
            options={chartOptions}
            series={series}
            type="bar"
            height={350}
          />
        ) : (
          <div className="text-center py-10 text-gray-500">
            Aucune donnée de prospects disponible pour la période sélectionnée
          </div>
        )}
      </div>

      <div className="mt-4 text-xs text-gray-500 text-right">
        Mis à jour le {format(new Date(), "dd/MM/yyyy à HH:mm", { locale: fr })}
      </div>
    </div>
  );
};

export default ProspectStatsChart;
